import { api } from './client'
import { SchemaValidationError, UsageSchema, parseJson } from './schemas'
import type {
  UserProfile,
  UserJobsPage,
  Usage,
  RedeemAccountCodeResponse,
  OwnedFilament,
  OwnedFilamentInput,
} from '@/types/api'

export async function getUserProfile(userId: string): Promise<UserProfile> {
  return api.get(`users/${userId}`).json()
}

export async function getUserUsage(userId: string): Promise<Usage> {
  const raw = await api.get(`users/${userId}/usage`).json()
  return parseJson(UsageSchema, raw, 'Usage')
}

export async function getUserJobs(userId: string, cursor?: string | null): Promise<UserJobsPage> {
  const searchParams = cursor ? { cursor } : undefined
  return api.get(`users/${userId}/jobs`, { searchParams }).json()
}

/** Redeem a single-use access code against the signed-in account, upgrading its
 * tier. Returns the account's new tier. Errors surface via the standard coded
 * envelope (invalid_request / code_already_redeemed / signup_only_code). */
export async function redeemAccountCode(
  userId: string,
  code: string,
): Promise<RedeemAccountCodeResponse> {
  return api.post(`users/${userId}/redeem-code`, { json: { code } }).json()
}

/** OF2 "My Filaments": maximum entries the server accepts (store.MaxOwnedFilaments). */
export const MAX_OWNED_FILAMENTS = 75

/** OF2: maximum serialized PUT body. The API WAF (`OversizeBody`, Rule 5 in
 * infra/lib/api-waf-stack.ts) blocks any larger body at the edge with a generic
 * 403 — not the app's error envelope — so the UI must check
 * `new TextEncoder().encode(JSON.stringify({ owned_filaments })).length` against
 * this before saving. Most lists of 75 fit, but very long names can hit this
 * ceiling before the entry cap. */
export const OWNED_FILAMENTS_MAX_BODY_BYTES = 16384

/** OF2 "My Filaments": replace the signed-in user's owned-filament list (full
 * replace; `[]` clears). Self-only. Resolves to the normalized list exactly as
 * the server stored it — use it to settle the `queryKeys.userProfile` cache
 * after an optimistic update.
 *
 * Errors (ky HTTPError, standard `{detail:{error,message,fields}}` body):
 * - 422 `validation_failed`: missing list, more than {@link MAX_OWNED_FILAMENTS}
 *   entries, or a bad entry. `detail.message` is human-readable ("Filament 4 has
 *   an invalid hex color."); `detail.fields` holds the JSON path
 *   (`owned_filaments[3].hex`).
 * - 400 `invalid_request`: malformed body.
 * - 403: another user's id, OR (deployed) a body over
 *   {@link OWNED_FILAMENTS_MAX_BODY_BYTES} blocked by the WAF.
 *
 * Throws a SchemaValidationError if a 2xx response lacks the list. */
export async function putOwnedFilaments(
  userId: string,
  ownedFilaments: OwnedFilamentInput[],
): Promise<OwnedFilament[]> {
  const res = await api
    .put(`users/${userId}/owned-filaments`, { json: { owned_filaments: ownedFilaments } })
    .json<{ owned_filaments?: OwnedFilament[] | null }>()
  if (!Array.isArray(res?.owned_filaments)) {
    throw new SchemaValidationError(
      'OwnedFilamentsResponse',
      'Schema validation failed for OwnedFilamentsResponse: owned_filaments: expected an array',
    )
  }
  return res.owned_filaments
}
